import { Injectable, inject } from '@angular/core';
import { catchError, Observable, of, switchMap, throwError, map, forkJoin, from } from 'rxjs';
import { isEmpty } from 'lodash';

// Types
import { ApiToCallOffline, ToFreeze } from '../../../shared/types/common';
import { FailedCall, OfflineFile, OfflineFileToStore } from '../../../shared/types/manageCalls';
import { TrackingActionsParams, UseTrialModeParams } from '../../../shared/types/params';
import { StorageKey } from '../../../shared/types/storage';

// Utils
import { downloadWithProgress } from '../../utils/download';

// Http
import { ApiService } from '../../http/api.service';

// Services
import { CommonService } from '../common/common.service';
import { LoggerService } from '../logger/logger.service';
import { StorageService } from '../storage/storage.service';
import { ActivationCodeService } from '../activation-code/activation-code.service';
import { TrialService } from '../trial/trial.service';
import { WsErrorService } from '../ws-error/ws-error.service';

@Injectable({ providedIn: 'root' })
export class OfflineService {
  private api = inject(ApiService);
  private common = inject(CommonService);
  private logger = inject(LoggerService);
  private storage = inject(StorageService);
  private activationCode = inject(ActivationCodeService);
  private trial = inject(TrialService);
  private wsError = inject(WsErrorService);

  public offlineFile: OfflineFileToStore;
  public isAlreadyGettingOfflineFile: boolean = false;
  public audiosContext: {
    normal: { buffer: AudioBufferSourceNode; context: AudioContext; gainNode: GainNode };
    infraUltra: { buffer: AudioBufferSourceNode; context: AudioContext; gainNode: GainNode };
  };

  getOfflineFile(fromComponent?: string, checkIsTrue: boolean = true): void {
    this.logger.consoleLog('OfflineService', 'getOfflineFile', {
      isAlreadyGettingOfflineFile: this.isAlreadyGettingOfflineFile,
    });

    if (!this.isAlreadyGettingOfflineFile) {
      this.logger.consoleLog('OfflineService', 'getOfflineFile', { fromComponent });

      this.isAlreadyGettingOfflineFile = true;
      this.innerGetOfflineFile('OfflineService 55').subscribe(this.offlineFileSub(checkIsTrue));
    }
  }

  obtainMp3s(): Promise<void> {
    this.logger.consoleLog('OfflineService', 'obtainMp3s');

    return new Promise<void>((resolve, reject) =>
      this.storage.getFromStorage(StorageKey.OfflineFile).subscribe({
        next: (data: OfflineFileToStore) => {
          if (data !== null) {
            this.offlineFile = data;
            if (this.wsError.isLoadingMp3) {
              this.wsError.removeWebServiceError();
            }

            if (this.audiosContext === undefined) {
              this.createAudioContext()
                .then(() => resolve())
                .catch((error) => {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  this.logger.consoleError('OfflineService', 'obtainMp3s', { error });
                  reject(error);
                });
            } else {
              resolve();
            }
          } else {
            this.logger.consoleError('OfflineService', 'obtainMp3', { data });
            this.getOfflineFile('obtainMp3s 84');
            resolve();
          }
        },
        error: () => {
          this.logger.consoleError('OfflineService', 'obtainMp3');
          void this.obtainMp3sAndStore('obtainMp3s 90').then(() => this.obtainMp3s());
        },
      }),
    );
  }

  addApiToCallWhenOnline(
    apiToCall: ApiToCallOffline['apiToCall'],
    params?: UseTrialModeParams | TrackingActionsParams,
  ): void {
    this.logger.consoleLog('OfflineService', 'addApiToCallWhenOnline');

    this.storage
      .getFromStorage<Array<ApiToCallOffline>>(StorageKey.ApisToCallOffline)
      .pipe(
        switchMap((apisToCallOffline) => {
          this.logger.consoleLog('OfflineService', 'addApiToCallWhenOnline', { apisToCallOffline });
          if (!apisToCallOffline) {
            return this.storage
              .setOnStorage(StorageKey.ApisToCallOffline, [] as Array<ApiToCallOffline>)
              .pipe(map(() => []));
          } else {
            return of(apisToCallOffline);
          }
        }),
      )
      .subscribe({
        next: (apisToCallOffline: Array<ApiToCallOffline>) => {
          this.logger.consoleLog('OfflineService', 'addApiToCallWhenOnline', { apisToCallOffline });

          const apiToUpdate = apisToCallOffline.findIndex((api) => api.apiToCall === apiToCall);

          if (apiToUpdate !== -1) {
            if (apisToCallOffline[apiToUpdate].params === undefined) {
              apisToCallOffline[apiToUpdate].params = [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            (apisToCallOffline[apiToUpdate].params as Array<UseTrialModeParams | TrackingActionsParams>).push(params);
          } else {
            apisToCallOffline.push({ apiToCall, params: [params] });
          }

          this.storage.setOnStorage(StorageKey.ApisToCallOffline, apisToCallOffline).subscribe();
        },
      });
  }

  getApisToCallWhenOnline(): Observable<Array<ApiToCallOffline>> {
    return this.storage.getFromStorage<Array<ApiToCallOffline>>(StorageKey.ApisToCallOffline).pipe(
      map((apisToCallOffline) => {
        if (apisToCallOffline && apisToCallOffline.length) {
          return apisToCallOffline;
        } else {
          return [];
        }
      }),
    );
  }

  removeApisToCallWhenOnline(): Observable<void> {
    return this.storage.removeFromStorage(StorageKey.ApisToCallOffline);
  }

  appOpenedOffline(): void {
    this.logger.consoleLog('OfflineService', 'appOpenedOffline', {
      configFile: this.common.configFile,
      offlineFile: this.offlineFile,
      audiosContext: this.audiosContext,
    });

    this.common.getUniqueDeviceID();
    this.common.getConfigFile().subscribe();

    this.storage.getFromStorage(StorageKey.OfflineFile).subscribe({
      next: (offlineFile: OfflineFile) => {
        if (!offlineFile || offlineFile?.OFFLINE_FILE_EXPIRATION.getTime() <= Date.now() || !this.audiosContext) {
          this.getOfflineFile('OfflineService 130');
        }
      },
    });

    this.storage.getFreezedState('OfflineService 168').subscribe({
      next: (lastState: ToFreeze) => {
        if (lastState !== null && !isEmpty(lastState)) {
          this.trial.hasRequestedTrialMode = lastState.hasRequestedTrialMode;
          this.trial.shouldEnableRequestTrial = lastState.shouldEnableRequestTrial;
          this.trial.trialsAvailable = lastState.trialsAvailable;
          this.trial.timeTrialExpiration = lastState.timeTrialExpiration;
          this.trial.isTrialMode = lastState.isTrialMode;
          this.trial.hasTrialExpired = lastState.hasTrialExpired;
          this.trial.hasTimeDuration = lastState.hasTimeDuration;

          this.activationCode.activationCodeValidity = { ...lastState.activationCodeValidity };
        }
      },
    });
  }

  freezeLastState(): void {
    this.logger.consoleLog('OfflineService', 'freezeLastState');

    let toFreeze = {
      hasRequestedTrialMode: this.trial.hasRequestedTrialMode,
      shouldEnableRequestTrial: this.trial.shouldEnableRequestTrial,
      trialsAvailable: this.trial.trialsAvailable,
      timeTrialExpiration: this.trial.timeTrialExpiration,
      isTrialMode: this.trial.isTrialMode,
      hasTrialExpired: this.trial.hasTrialExpired,
      hasTimeDuration: this.trial.hasTimeDuration,
    } as ToFreeze;

    if (this.activationCode.activationCodeValidity) {
      toFreeze = { ...toFreeze, activationCodeValidity: this.activationCode.activationCodeValidity };
    }

    this.storage.setOnStorage(StorageKey.FreezeLastState, toFreeze).subscribe();
  }

  private offlineFileSub(checkIsTrue: boolean = true): { next: () => void; error: () => void } {
    this.logger.consoleLog('OfflineService', 'offlineFileSub', { checkIsTrue, offlineFile: this.offlineFile });

    return {
      next: (): void => {
        if (checkIsTrue) {
          if (!this.offlineFile?.NORMAL?.buffer && !this.offlineFile?.INFRAULTRA?.buffer) {
            this.logger.consoleError('OfflineService', 'offlineFileSub', 'setWebServiceError');
            this.wsError.setWebServiceError('COMMON.DOWNLOADING_MP3', true, true);
          } else if (this.wsError.isLoadingMp3) {
            this.wsError.removeWebServiceError(true);
          }
        }
      },
      error: (): void => {
        this.logger.consoleLog('OfflineService', 'offlineFileSub', 'getOfflineFile => blockedApp');
      },
    };
  }

  private createAudioContext(): Promise<void> {
    this.logger.consoleLog('OfflineService', 'createAudioContext', { offlineFile: this.offlineFile });

    return new Promise((resolve, reject) => {
      this.audiosContext = { normal: undefined, infraUltra: undefined };
      this.logger.consoleLog('OfflineService', 'createAudioContext', { audiosContext: this.audiosContext });

      const getNormalAudioContext = new Promise<void>((innerResolve) => {
        const audioContext = new AudioContext();

        void audioContext.decodeAudioData(
          this.offlineFile.NORMAL.buffer,
          (decodedData) => {
            const gainNode = audioContext.createGain(); // Create a gainNode reference.
            gainNode.connect(audioContext.destination); // Add context to gainNode

            const track = audioContext.createBufferSource();
            track.buffer = decodedData;
            track.loop = true;
            track.connect(gainNode);

            gainNode.gain.value = 0; // 0% VOLUME RANGE OF VALUE IS 0-1

            if (this.audiosContext === undefined) {
              this.audiosContext = { normal: undefined, infraUltra: undefined };
            }
            this.audiosContext.normal = { buffer: track, context: audioContext, gainNode };

            this.logger.consoleLog('OfflineService', 'createAudioContext getNormalAudioContext', {
              audiosContext: this.audiosContext,
            });
            innerResolve();
          },
          (error) => {
            this.logger.consoleError('OfflineService', 'createAudioContext getNormalAudioContext', { error });
          },
        );
      });

      const getInfraUltraAudioContext = new Promise<void>((innerResolve) => {
        const audioContext = new AudioContext();
        void audioContext.decodeAudioData(
          this.offlineFile.INFRAULTRA.buffer,
          (decodedData) => {
            const gainNode = audioContext.createGain(); // Create a gainNode reference.
            gainNode.connect(audioContext.destination); // Add context to gainNode

            const track = audioContext.createBufferSource();
            track.buffer = decodedData;
            track.loop = true;
            track.connect(gainNode);

            gainNode.gain.value = 0; // 0% VOLUME RANGE OF VALUE IS 0-1

            if (this.audiosContext === undefined) {
              this.audiosContext = { normal: undefined, infraUltra: undefined };
            }
            this.audiosContext.infraUltra = { buffer: track, context: audioContext, gainNode };

            this.logger.consoleLog('OfflineService', 'createAudioContext getInfraUltraAudioContext', {
              audiosContext: this.audiosContext,
            });
            innerResolve();
          },
          (error) => {
            this.logger.consoleError('OfflineService', 'createAudioContext getInfraUltraAudioContext', { error });
          },
        );
      });

      Promise.all([getNormalAudioContext, getInfraUltraAudioContext])
        .then(() => {
          this.logger.consoleLog('OfflineService', 'createAudioContext', { audiosContext: this.audiosContext });
          resolve();
        })
        .catch((error: FailedCall) => {
          this.logger.consoleError('OfflineService', 'createAudioContext', { error });
          reject(error);
        });
    });
  }

  private innerGetOfflineFile(fromComponent?: string): Observable<OfflineFileToStore> {
    return this.storage.getFromStorage<OfflineFileToStore>(StorageKey.OfflineFile).pipe(
      switchMap((offlineFile: OfflineFileToStore) => {
        this.audiosContext = undefined;
        this.logger.consoleLog('OfflineService', 'innerGetOfflineFile', { fromComponent });

        if (!offlineFile || offlineFile?.OFFLINE_FILE_EXPIRATION.getTime() <= Date.now()) {
          this.storage.removeFromStorage(StorageKey.FreezeLastState).subscribe();
          this.logger.consoleLog('OfflineService', 'innerGetOfflineFile', 'Need to download new file');

          return this.api.appFunctionalities.getOfflineAudioFile().pipe(
            switchMap((offFile: OfflineFile) => {
              this.offlineFile = {
                NORMAL: { url: undefined, buffer: undefined },
                INFRAULTRA: { url: undefined, buffer: undefined },
              };
              this.offlineFile['NORMAL']['url'] = offFile.NORMAL;
              this.offlineFile['INFRAULTRA']['url'] = offFile.INFRAULTRA;
              this.offlineFile.OFFLINE_FILE_EXPIRATION = new Date(offFile.OFFLINE_FILE_EXPIRATION as unknown as string);

              return from(this.obtainMp3sAndStore('innerGetOfflineFile 329')).pipe(
                map(() => {
                  this.isAlreadyGettingOfflineFile = false;
                  return offlineFile;
                }),
              );
            }),
            catchError((error: FailedCall) => {
              this.isAlreadyGettingOfflineFile = false;
              this.logger.consoleError('OfflineService', 'innerGetOfflineFile', { error });
              return throwError(() => error);
            }),
          );
        } else {
          this.logger.consoleLog(
            'OfflineService',
            'innerGetOfflineFile',
            'Using local offline file. It expires on ' + new Date(offlineFile?.OFFLINE_FILE_EXPIRATION).toISOString(),
          );
          this.offlineFile = offlineFile;

          if (this.wsError.isLoadingMp3) {
            this.wsError.removeWebServiceError();
          }

          return from(this.createAudioContext()).pipe(
            map(() => {
              this.isAlreadyGettingOfflineFile = false;
              return offlineFile;
            }),
          );
        }
      }),
    );
  }

  private obtainMp3sAndStore(fromComponent?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.logger.consoleLog('OfflineService', 'obtainMp3sAndStore', { offlineFile: this.offlineFile, fromComponent });

      const standardDownload = this.api.appFunctionalities
        .getAudioFileBuffer(this.offlineFile.NORMAL.url)
        .pipe(downloadWithProgress());
      const silentDownload = this.api.appFunctionalities
        .getAudioFileBuffer(this.offlineFile.INFRAULTRA.url)
        .pipe(downloadWithProgress());

      forkJoin([standardDownload, silentDownload]).subscribe({
        next: ([standard, silent]) => {
          this.offlineFile.NORMAL.buffer = standard.content;
          this.offlineFile.INFRAULTRA.buffer = silent.content;

          this.storage.setOnStorage(StorageKey.OfflineFile, this.offlineFile).subscribe(() => {
            this.logger.consoleLog('OfflineService', 'obtainMp3sAndStore', 'Local files setted');
            this.wsError.removeWebServiceError(true);

            this.createAudioContext()
              .then(() => resolve())
              .catch((error) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                this.logger.consoleError('OfflineService', 'obtainMp3sAndStore', { error });
                reject(error);
              });
          });
        },
        error: (error: FailedCall) => {
          this.logger.consoleError('OfflineService', 'obtainMp3sAndStore', { error });
          reject(error);
        },
      });
    });
  }
}
